// import React, { useEffect, useState } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import {
//   Box, Typography, Paper, TextField, Chip, IconButton, Tooltip
// } from '@mui/material';
// import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
// import DataTable from '../components/common/DataTable';
// import LoadingSpinner from '../components/common/LoadingSpinner';
// import { adminService } from '../services/admin';

// const BusinessUsersPage = () => {
//   const { tenantId } = useParams(); // tenantId = businessId e.g. "SHOPHUB_6322..."
//   const navigate = useNavigate();

//   const [users, setUsers] = useState([]);
//   const [total, setTotal] = useState(0);
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(10);
//   const [search, setSearch] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [stats, setStats] = useState({});

//   useEffect(() => {
//     fetchUsers();
//   }, [page, rowsPerPage, search]);

//   const fetchUsers = async () => {
//     try {
//       setLoading(true);
//       const res = await adminService.getBusinessUsers(tenantId, page, rowsPerPage, search);
//       setUsers(res.data || []);
//       setTotal(res.pagination?.total || 0);
//       setStats(res.stats || {});
//     } catch (err) {
//       console.error('Failed to fetch users:', err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const columns = [
//     { field: 'srNo', headerName: '#' },
//     { field: 'name', headerName: 'Name' },
//     { field: 'email', headerName: 'Email' },
//     { field: 'mobile', headerName: 'Mobile' },
//     { field: 'uniqueUserId', headerName: 'User ID' },
//     {
//       field: 'qrVerified',
//       headerName: 'QR Verified',
//       render: (value) => (
//         <Chip
//           label={value}
//           size="small"
//           color={value?.includes('Yes') ? 'success' : 'default'}
//           variant="outlined"
//         />
//       ),
//     },
//     { field: 'createdAt', headerName: 'Created At' },
//   ];

//   return (
//     <Box sx={{ minHeight: '100%', background: '#2563EB', p: 3 }}>
//       <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
//         <Tooltip title="Back to Businesses">
//           <IconButton onClick={() => navigate('/admin/businesses')}>
//             <ArrowBackIcon />
//           </IconButton>
//         </Tooltip>
//         <Box>
//           <Typography variant="h5" sx={{
//             fontWeight: 700,
//             background: '#2563EB',
//             WebkitBackgroundClip: 'text',
//             WebkitTextFillColor: 'transparent'
//           }}>
//             Business Users
//           </Typography>
//           <Typography variant="body2" sx={{ color: '#64748b' }}>
//             Merchant ID: {tenantId}
//           </Typography>
//         </Box>
//       </Box>

//       {/* Stats */}
//       <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
//         <Paper elevation={0} sx={{
//           px: 3, py: 1.5, borderRadius: 2,
//           border: '1px solid #3182ce30', background: '#3182ce10'
//         }}>
//           <Typography variant="h6" sx={{ fontWeight: 700, color: '#3182ce' }}>
//             {stats.total ?? '—'}
//           </Typography>
//           <Typography variant="caption" sx={{ color: '#64748b' }}>Total Users</Typography>
//         </Paper>
//       </Box>

//       <Paper elevation={0} sx={{
//         borderRadius: 3, border: '1px solid rgba(49,130,206,0.2)',
//         background: '#2563EB', p: 3
//       }}>
//         <TextField
//           fullWidth
//           placeholder="Search by name, email, or mobile..."
//           value={search}
//           onChange={(e) => { setSearch(e.target.value); setPage(0); }}
//           InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
//           sx={{ mb: 2 }}
//         />

//         <Box sx={{ position: 'relative' }}>
//           {loading && (
//             <Box sx={{
//               position: 'absolute', inset: 0, display: 'flex',
//               alignItems: 'center', justifyContent: 'center',
//               backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1, borderRadius: 1
//             }}>
//               <LoadingSpinner size={40} />
//             </Box>
//           )}
//           <DataTable
//             columns={columns}
//             data={users}
//             page={page}
//             rowsPerPage={rowsPerPage}
//             totalRows={total}
//             onPageChange={setPage}
//             onRowsPerPageChange={setRowsPerPage}
//           />
//         </Box>
//       </Paper>
//     </Box>
//   );
// };

// export default BusinessUsersPage;

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminService } from '../services/admin';

// 'linkedAt'/'createdAt' arrive pre-formatted from the API as en-IN locale
// text (D/M/YYYY, no leading zeros) rather than a raw timestamp, so a plain
// string sort would order it lexically instead of chronologically. Parse it
// back into a comparable number so the column header sorts by actual date.
const parseFormattedDate = (value) => {
  if (!value || value === 'N/A') return null;
  const [day, month, year] = value.split('/').map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day).getTime();
};

const BusinessUsersPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { tenantId } = useParams(); // businessId string
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await adminService.getBusinessUsers(tenantId, page, rowsPerPage, search);
      setUsers(res.data || []);
      setTotal(res.pagination?.total || 0);
      setStats(res.stats || {});
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'srNo', headerName: 'Sr', width: 60 },
    { field: 'email', headerName: 'Email', width: 200, sortable: true },
     {
      field: '_id',
      headerName: 'Subscriber ID', // ✅ renamed
      width: 160,
      sortable: true,
      render: (val) => (
        <Tooltip title={val}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {val?.substring(0, 12)}…
          </span>
        </Tooltip>
      ),
    },
    { field: 'phoneNumber', headerName: 'Phone', width: 150, sortable: true },
    {
      field: 'deviceId',
      headerName: 'Device ID',
      width: 160,
      sortable: true,
      render: (val) => (
        <Tooltip title={val}>
          <span>{val?.length > 12 ? `${val.substring(0, 12)}…` : val}</span>
        </Tooltip>
      ),
    },
    { field: 'deviceType', headerName: 'Device Type', width: 120, sortable: true },
    {
      field: 'isDeviceActive',
      headerName: 'Active',
      width: 90,
      sortable: true,
      render: (val) => (
        <Chip
          label={val === 'Active' ? 'Active' : 'Inactive'}
          size="small"
          color={val === 'Active' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'smsVerified',
      headerName: 'SMS',
      width: 100,
      sortable: true,
      render: (val) => (
        <Chip
          label={val === 'Verified' ? 'Verified' : 'Pending'}
          size="small"
          color={val === 'Verified' ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'emailVerified',
      headerName: 'Email',
      width: 100,
      sortable: true,
      render: (val) => (
        <Chip
          label={val === 'Verified' ? 'Verified' : 'Pending'}
          size="small"
          color={val === 'Verified' ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'globalBlocked',
      headerName: 'Global',
      width: 100,
      sortable: true,
      render: (val) => (
        <Chip
          label={val === 'Blocked' ? 'Blocked' : 'Active'}
          size="small"
          color={val === 'Blocked' ? 'error' : 'success'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'businessBlocked',
      headerName: 'Business',
      width: 100,
      sortable: true,
      render: (val) => (
        <Chip
          label={val === 'Blocked' ? 'Blocked' : 'Active'}
          size="small"
          color={val === 'Blocked' ? 'error' : 'success'}
          variant="outlined"
        />
      ),
    },
    { field: 'linkedAt', headerName: 'Linked On', width: 120, sortable: true, sortValue: (v) => parseFormattedDate(v) },
    { field: 'createdAt', headerName: 'Joined', width: 120, sortable: true, sortValue: (v) => parseFormattedDate(v) },
  ];

  return (
    <Box sx={{ minHeight: '100%', background: theme.palette.background.default, p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title="Back to Businesses">
          <IconButton onClick={() => navigate('/admin/businesses')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Linked Users
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Business ID: {tenantId}
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.4 : 0.19)}`,
            background: alpha(theme.palette.primary.main, isDark ? 0.16 : 0.06),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {stats.total ?? total ?? 0}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Total Linked Users
          </Typography>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
          p: 3,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search by email, phone, or device ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255,255,255,0.7)',
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <LoadingSpinner size={40} />
            </Box>
          )}
          <DataTable
            columns={columns}
            data={users}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default BusinessUsersPage;